/**
 * Period-comparison types for analytics responses.
 *
 * Two primitives drive every dashboard's prior-period overlay:
 *
 * - `displacement`: shift back N calendar units (day/week/month/year),
 *   day-aligned.
 * - `time_delta`: shift back by the length of the selected range.
 *
 * Every summary endpoint extends `PeriodComparable<Self>` so consumers always
 * read the same four fields:
 *
 * - `priorPeriodSummary`: same shape as the inheriting summary, populated with
 *   the comparison range's totals.
 * - `priorPeriodStartDate` / `priorPeriodEndDate`: ISO dates of the comparison
 *   range (whatever the active spec resolved to).
 * - `summaryDeltas`: per-numeric-field deltas with `pctChange` (`null` when
 *   both sides are zero, `Infinity` when prior is zero but current isn't —
 *   render that as a "new" badge).
 *
 * All four fields are optional — when the user disables the comparison (or
 * the dashboard doesn't support it), they're absent.
 */
import { endOfMonth, subDays, subMonths, subWeeks, subYears } from "date-fns";

export type ComparisonUnit = "day" | "week" | "month" | "year";

export type DisplacementComparison = {
  type: "displacement";
  amount: number;
  unit: ComparisonUnit;
};

export type TimeDeltaComparison = {
  type: "time_delta";
};

export type ComparisonSpec = DisplacementComparison | TimeDeltaComparison;

export const PREVIOUS_MONTH_COMPARISON: ComparisonSpec = {
  type: "displacement",
  amount: 1,
  unit: "month",
};

// Off by default: each comparison runs a second full query for the prior period.
export const DEFAULT_COMPARISON: ComparisonSpec | null = null;

export interface ComparisonSummaryDelta {
  field: string;
  current: number;
  prior: number;
  delta: number;
  pctChange: number | null;
}

export interface PeriodComparable<TSelf> {
  priorPeriodSummary?: TSelf | null;
  priorPeriodStartDate?: string | null;
  priorPeriodEndDate?: string | null;
  summaryDeltas?: ComparisonSummaryDelta[];
}

/**
 * Look up a delta by field name (camelCase keys, matching the summary field).
 *
 * Returns `undefined` when no comparison is available so callers can fall back
 * to rendering the bare metric.
 */
export function findDelta<TSelf>(
  summary: PeriodComparable<TSelf> | null | undefined,
  field: string,
): ComparisonSummaryDelta | undefined {
  return summary?.summaryDeltas?.find((d) => d.field === field);
}

/**
 * Flatten a `ComparisonSpec` (or `null`) into the three query parameters the
 * dashboard endpoints accept.
 */
export function comparisonToQuery(spec: ComparisonSpec | null): {
  comparison_type: string;
  comparison_amount: number;
  comparison_unit: ComparisonUnit;
} {
  if (spec === null) {
    return {
      comparison_type: "none",
      comparison_amount: 1,
      comparison_unit: "month",
    };
  }
  if (spec.type === "time_delta") {
    return {
      comparison_type: "time_delta",
      comparison_amount: 1,
      comparison_unit: "month",
    };
  }
  return {
    comparison_type: "displacement",
    comparison_amount: spec.amount,
    comparison_unit: spec.unit,
  };
}

/**
 * Frontend mirror of `resolve_comparison_range` (`decoda/services/
 * analytics_dashboards/comparison.py`). Used by `ComparisonPicker` to preview
 * the prior-period dates next to each option without a round-trip.
 *
 * Day-unit displacement is a straight `subDays`. Month/year displacement uses
 * `date-fns`' month/year subtractors (which already handle leap-year and
 * end-of-month clamping the same way `relativedelta` does). When the input is
 * a full calendar month (1st through the last day), the result is snapped to
 * the full target month so May 1–31 → Apr 1–30 instead of Apr 1–May 1.
 *
 * `time_delta`: prior period is the equal-length range ending the day before
 * the current range starts.
 */
export function resolveComparisonRange(
  start: Date,
  end: Date,
  spec: ComparisonSpec,
): { start: Date; end: Date } {
  if (spec.type === "time_delta") {
    const durationDays =
      Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
    return {
      start: subDays(start, durationDays),
      end: subDays(start, 1),
    };
  }

  const sub = (d: Date): Date => {
    switch (spec.unit) {
      case "day":
        return subDays(d, spec.amount);
      case "week":
        return subWeeks(d, spec.amount);
      case "month":
        return subMonths(d, spec.amount);
      case "year":
        return subYears(d, spec.amount);
    }
  };

  const newStart = sub(start);
  let newEnd = sub(end);

  if (
    (spec.unit === "month" || spec.unit === "year") &&
    start.getDate() === 1 &&
    end.getDate() === endOfMonth(start).getDate()
  ) {
    newEnd = endOfMonth(newEnd);
  }

  return { start: newStart, end: newEnd };
}
